import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, WarningCircle } from "@phosphor-icons/react";

export default function PublicForm() {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({ student_name: "", parent_name: "", parent_email: "" });
  const [signature, setSignature] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/public/forms/${formId}`)
      .then(({ data }) => setForm(data))
      .catch((e) => setError(apiError(e.response?.data?.detail) || "This form is not available."));
  }, [formId]);

  const set = (label, v) => setAnswers({ ...answers, [label]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/public/forms/${formId}/submit`, { ...meta, answers, signature });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  if (error)
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <div className="text-center max-w-md" data-testid="form-error">
          <WarningCircle size={64} weight="fill" className="mx-auto text-[#FBBF24]" />
          <h1 className="mt-6 text-3xl font-bold tracking-tight">Form unavailable</h1>
          <p className="mt-3 text-muted-foreground">{error}</p>
          <p className="mt-6 text-sm text-muted-foreground">Call (501) 904-7103 and we'll help.</p>
        </div>
      </div>
    );

  if (done)
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6">
        <div className="text-center max-w-md tpl-fade-up" data-testid="form-success">
          <CheckCircle size={72} weight="fill" className="mx-auto text-[#10B981]" />
          <h1 className="mt-8 text-4xl font-bold tracking-tight">Thank you!</h1>
          <p className="mt-4 text-muted-foreground">
            Your {form?.title} has been submitted to Teach Play to Learn. We'll be in touch if we need
            anything else.
          </p>
        </div>
      </div>
    );

  if (!form) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  const field = (f) => {
    const v = answers[f.label] ?? "";
    const tid = `form-field-${f.label.replace(/\s+/g, "-").toLowerCase()}`;
    if (f.type === "long_text")
      return <Textarea value={v} onChange={(e) => set(f.label, e.target.value)}
                       className="mt-2 rounded-xl min-h-24" data-testid={tid} />;
    if (f.type === "yes_no")
      return (
        <Select value={v} onValueChange={(x) => set(f.label, x)}>
          <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid={tid}>
            <SelectValue placeholder="Choose" />
          </SelectTrigger>
          <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
        </Select>
      );
    if (f.type === "select" || f.type === "multiselect")
      return (
        <Select value={v} onValueChange={(x) => set(f.label, x)}>
          <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid={tid}>
            <SelectValue placeholder="Choose" />
          </SelectTrigger>
          <SelectContent>
            {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    if (f.type === "checkbox")
      return (
        <label className="mt-2 flex items-center gap-3 text-sm font-semibold">
          <input type="checkbox" checked={v === "Yes"}
                 onChange={(e) => set(f.label, e.target.checked ? "Yes" : "")} data-testid={tid} />
          I agree
        </label>
      );
    return <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  value={v} onChange={(e) => set(f.label, e.target.value)}
                  className="mt-2 h-11 rounded-xl" data-testid={tid} />;
  };

  return (
    <div className="min-h-screen bg-background px-6 py-14">
      <div className="mx-auto max-w-2xl">
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">{form.kind}</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight" data-testid="form-title">{form.title}</h1>
        {form.description && <p className="mt-3 text-muted-foreground">{form.description}</p>}
        <p className="mt-2 text-sm text-muted-foreground">{form.program?.name}</p>
        {form.due_date && (
          <p className="mt-2 text-sm font-bold text-[#EF4444]">Please complete by {form.due_date}</p>
        )}

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl border border-border p-8 space-y-5"
              data-testid="public-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-sm font-bold">Child's full name *</Label>
              <Input required value={meta.student_name}
                     onChange={(e) => setMeta({ ...meta, student_name: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="form-student-name" />
            </div>
            <div>
              <Label className="text-sm font-bold">Your name</Label>
              <Input value={meta.parent_name} onChange={(e) => setMeta({ ...meta, parent_name: e.target.value })}
                     className="mt-2 h-11 rounded-xl" data-testid="form-parent-name" />
            </div>
          </div>
          <div>
            <Label className="text-sm font-bold">Your email *</Label>
            <Input type="email" required value={meta.parent_email}
                   onChange={(e) => setMeta({ ...meta, parent_email: e.target.value })}
                   className="mt-2 h-11 rounded-xl" data-testid="form-parent-email" />
          </div>

          {(form.fields || []).map((f, i) => (
            <div key={i}>
              <Label className="text-sm font-bold">
                {f.label}{f.required ? " *" : ""}
              </Label>
              {field(f)}
            </div>
          ))}

          {form.require_signature && (
            <div>
              <Label className="text-sm font-bold">Type your full name to sign *</Label>
              <Input required value={signature} onChange={(e) => setSignature(e.target.value)}
                     className="mt-2 h-14 rounded-xl font-display text-xl" placeholder="Your signature"
                     data-testid="form-signature" />
              <p className="mt-2 text-xs text-muted-foreground">
                By signing you confirm the information above is accurate.
              </p>
            </div>
          )}

          <Button type="submit" disabled={busy}
                  className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                  data-testid="form-submit-btn">{busy ? "Submitting…" : "Submit"}</Button>
        </form>
      </div>
    </div>
  );
}
